import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { CheckCircle2, ShieldAlert } from 'lucide-react';
import { ContactlessConfig, ContactlessSession, ContactlessSessionCreate, ContactlessSignInResult, ContactlessStart, ContactlessState } from '@cloudsign/shared';
import type { ImSafe } from '@cloudsign/shared';
import { brandStyle } from '../../lib/branding';
import { t } from '../../lib/i18n';
import { Alert, Button } from '../../components/ui';
import { useLinkBranding, useNoReferrer } from '../public-links/api';
import { LanguagePicker, VisitorLanguageProvider, useVisitorLanguage } from '../public-p4/locale';
import { LinkProblem, PageLoading, Section } from '../public-links/PublicShell';
import { SignInFlow } from './SignInFlow';
import type { SignedIn } from './SignInFlow';
import { QuestionsStep, askableFields } from './steps';
import { call, isSessionEnded, problemText, publicBase, rememberStore, sessionStore } from './api';
import type { LinkKind } from './api';
import { newKey, timeIn } from './util';

type Start = ContactlessStart;

// ---------- Frame ----------

function Shell({ token, start, children }: { token: string; start: Start | undefined; children: ReactNode }) {
  const branding = useLinkBranding(token).data;
  return (
    <VisitorLanguageProvider scope={branding?.tenantSlug ?? 'default'} languages={branding?.languages}>
      <ShellFrame slug={branding?.tenantSlug ?? null} start={start}>
        {children}
      </ShellFrame>
    </VisitorLanguageProvider>
  );
}

function ShellFrame({ slug, start, children }: { slug: string | null; start: Start | undefined; children: ReactNode }) {
  useNoReferrer();
  const { vt } = useVisitorLanguage();
  const style = brandStyle({ primaryColour: start?.branding.accent ?? '', accentColour: '' });
  return (
    <div style={style} className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-xl items-center gap-3 px-4 py-3">
          {start?.branding.logoUrl ? <img src={start.branding.logoUrl} alt={t('w21.logoAlt', { name: start.tenantName })} referrerPolicy="no-referrer" className="h-9 w-auto max-w-[8rem] object-contain" /> : null}
          <span className="truncate text-base font-semibold">{start ? start.tenantName : ''}</span>
          <LanguagePicker />
        </div>
      </header>
      <main className="mx-auto max-w-xl space-y-4 px-4 py-6">{children}</main>
      <footer className="mx-auto flex max-w-xl flex-wrap items-center gap-x-4 gap-y-1 px-4 pb-8 text-xs text-slate-600">
        <span>{t('w21.footer')}</span>
        {slug ? (
          <Link to={`/${slug}/privacy`} className="font-medium text-slate-800 underline">
            {vt('public.yourData')}
          </Link>
        ) : null}
      </footer>
    </div>
  );
}

// ---------- Evacuation: shown first, with a big "I'm safe" button ----------

function EvacuationAlert({ session, state }: { session: string; state: ContactlessState }) {
  const [safeAt, setSafeAt] = useState<string | null>(null);
  const [key] = useState(newKey);
  const safe = useMutation({
    mutationFn: () => {
      const body: ImSafe = { idempotencyKey: key, occurredAt: new Date().toISOString(), pointId: null, location: null };
      return call('POST', '/evacuation/contactless/safe', { session, body });
    },
    onSuccess: () => setSafeAt(new Date().toISOString()),
  });
  const marked = safeAt ?? state.markedSafeAt;
  return (
    <section role="alert" aria-live="assertive" className="space-y-4 rounded-lg border-2 border-red-700 bg-red-50 p-4 text-red-950">
      <h1 className="flex items-center gap-2 text-2xl font-bold">
        <ShieldAlert aria-hidden="true" className="h-8 w-8 shrink-0" />
        {t('w21.evac.title')}
      </h1>
      <p className="text-lg font-medium">{t(state.alert.kind === 'drill' ? 'w21.evac.drill' : 'w21.evac.leave')}</p>
      {state.alert.points.length > 0 ? (
        <div>
          <h2 className="font-semibold">{t('w21.evac.points')}</h2>
          <ul className="mt-1 space-y-1">
            {state.alert.points.map((p) => (
              <li key={p.id}>
                <span className="font-medium">{p.name}</span>
                {p.directions ? <span className="block text-sm">{p.directions}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {marked ? (
        <p className="flex items-center gap-2 text-lg font-semibold text-green-900">
          <CheckCircle2 aria-hidden="true" className="h-6 w-6" />
          {t('w21.evac.markedSafe')}
        </p>
      ) : state.canMarkSafe ? (
        <>
          <Button variant="danger" className="min-h-20 w-full text-2xl font-bold" disabled={safe.isPending} onClick={() => safe.mutate()}>
            {t('w21.evac.imSafe')}
          </Button>
          <p className="text-sm">{t('w21.evac.imSafeHelp')}</p>
          {safe.isError ? <Alert>{problemText(safe.error)}</Alert> : null}
        </>
      ) : (
        <p className="text-base">{t('w21.evac.notSignedIn')}</p>
      )}
    </section>
  );
}

// ---------- Start ----------

function locate(): Promise<{ lat: number; lng: number; accuracyM: number }> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) return reject(new Error('no-geolocation'));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracyM: Math.min(10000, Math.max(0, Math.round(p.coords.accuracy))) }),
      (e) => reject(new Error(e.code === 1 ? 'denied' : 'failed')),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });
}

function StartCard({ start, kind, token, ended, onSession, onForget, hasRemember }: { start: Start; kind: LinkKind; token: string; ended: boolean; onSession: (s: ContactlessSession) => void; onForget: () => void; hasRemember: boolean }) {
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const begin = async () => {
    setBusy(true);
    setProblem(null);
    try {
      // Location is asked for here, on the one screen that needs it, and only when the site asks.
      let location: { lat: number; lng: number; accuracyM: number } | null = null;
      if (start.requireNearSite) {
        try {
          location = await locate();
        } catch (e) {
          setProblem(t(e instanceof Error && e.message === 'denied' ? 'w21.start.locationDenied' : 'w21.start.locationFailed'));
          return;
        }
      }
      const body = ContactlessSessionCreate.parse({ rememberToken: rememberStore.get(), location });
      const s = await call('POST', `${publicBase(kind)}/${encodeURIComponent(token)}/session`, { body, schema: ContactlessSession });
      onSession(s);
    } catch (e) {
      setProblem(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const modeText = start.point ? t(`w21.start.mode.${start.point.mode}`, { point: start.point.name }) : null;
  return (
    <Section>
      <h1 className="text-2xl font-semibold">{t('w21.start.title', { site: start.siteName })}</h1>
      {modeText ? <p className="text-lg">{modeText}</p> : null}
      {ended ? <Alert kind="info">{t('w21.start.ended')}</Alert> : null}
      <p className="text-base text-slate-800">{t('w21.start.privacy')}</p>
      {start.requireNearSite ? <p className="text-base text-slate-800">{t('w21.start.locationWhy')}</p> : null}
      {problem ? <Alert>{problem}</Alert> : null}
      <Button variant="primary" className="min-h-14 w-full text-lg" disabled={busy} onClick={() => void begin()}>
        {start.requireNearSite ? t('w21.start.buttonLocation') : t('w21.start.button')}
      </Button>
      {hasRemember ? (
        <button type="button" className="min-h-11 text-base text-brand underline" onClick={onForget}>
          {t('w21.forget.button')}
        </button>
      ) : null}
    </Section>
  );
}

// ---------- Thanks and sign out ----------

function Thanks({ message, remembered, onForget, canSignOut, onSignOut, outDone }: { message: string; remembered: boolean; onForget: () => void; canSignOut: boolean; onSignOut: () => void; outDone: boolean }) {
  return (
    <Section>
      <h1 className="flex items-center gap-2 text-2xl font-semibold">
        <CheckCircle2 aria-hidden="true" className="h-7 w-7 text-green-800" />
        {t(outDone ? 'w21.thanks.outTitle' : 'w21.thanks.title')}
      </h1>
      <p className="whitespace-pre-line text-lg">{message}</p>
      {!outDone && canSignOut ? <p className="text-base text-slate-800">{t('w21.thanks.signOutHint')}</p> : null}
      {!outDone && canSignOut ? (
        <Button className="min-h-12 w-full text-base" onClick={onSignOut}>
          {t('w21.signOut.open')}
        </Button>
      ) : null}
      {remembered ? (
        <>
          <p className="text-sm text-slate-700">{t('w21.remember.saved')}</p>
          <button type="button" className="min-h-11 text-base text-brand underline" onClick={onForget}>
            {t('w21.forget.button')}
          </button>
        </>
      ) : null}
    </Section>
  );
}

function SignOutCard({ session, config, open, timeZone, onDone }: { session: string; config: ContactlessConfig | undefined; open: NonNullable<ContactlessSession['openVisit']>; timeZone: string; onDone: (r: ContactlessSignInResult) => void }) {
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [key] = useState(newKey);
  const group = config?.groups.find((g) => g.name === open.groupName);
  const q = group?.signOutSteps.find((s) => s.key === 'questions');
  const questions = q && askableFields(q.config as { fields?: unknown[] }).length > 0 ? q : null;

  const out = async (steps: Record<string, unknown>) => {
    setBusy(true);
    setProblem(null);
    try {
      onDone(await call('POST', '/contactless/session/sign-out', { session, body: { idempotencyKey: key, steps }, schema: ContactlessSignInResult }));
    } catch (e) {
      setProblem(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section>
      <h1 className="text-2xl font-semibold">{t('w21.signOut.title')}</h1>
      <p className="text-base">{t('w21.signOut.since', { time: timeIn(open.signedInAt, timeZone), group: open.groupName })}</p>
      {questions ? (
        <QuestionsStep config={questions.config as { fields?: unknown[] }} session={session} busy={busy} nextLabel={t('w21.signOut.button')} onNext={(v) => void out({ questions: v })} />
      ) : (
        <Button variant="primary" className="min-h-14 w-full text-lg" disabled={busy} onClick={() => void out({})}>
          {t('w21.signOut.button')}
        </Button>
      )}
      {problem ? <Alert>{problem}</Alert> : null}
    </Section>
  );
}

// ---------- The page ----------

function loadSession(kind: LinkKind, token: string): ContactlessSession | null {
  const raw = sessionStore.get(kind, token);
  if (!raw) return null;
  try {
    const p = ContactlessSession.safeParse(JSON.parse(raw));
    return p.success && new Date(p.data.expiresAt).getTime() > Date.now() ? p.data : null;
  } catch {
    return null;
  }
}

/**
 * /k/:token (site QR) and /s/:token (sign-in point). No sign-in, no portal shell: the signed link starts a short session, and
 * that session token (kept in sessionStorage) is the only credential for the calls after it. Nothing here shows other people's data.
 */
export function ContactlessPage({ kind }: { kind: LinkKind }) {
  const { token = '' } = useParams();
  const startQ = useQuery({
    queryKey: ['contactless', kind, token, 'start'],
    queryFn: () => call('GET', `${publicBase(kind)}/${encodeURIComponent(token)}`, { schema: ContactlessStart }),
    retry: false,
  });
  const [session, setSessionState] = useState<ContactlessSession | null>(() => loadSession(kind, token));
  const [ended, setEnded] = useState(false);
  const [signedIn, setSignedIn] = useState<SignedIn | null>(null);
  const [signedOut, setSignedOut] = useState<ContactlessSignInResult | null>(null);
  const [wantsOut, setWantsOut] = useState(false);
  const [hasRemember, setHasRemember] = useState(() => rememberStore.get() !== null);
  const [rememberedNow, setRememberedNow] = useState(false);

  const setSession = (s: ContactlessSession | null) => {
    setSessionState(s);
    if (s) sessionStore.set(kind, token, JSON.stringify(s));
    else sessionStore.clear(kind, token);
  };
  const endSession = () => {
    setSession(null);
    setEnded(true);
    setSignedIn(null);
  };

  const sess = session?.token ?? null;
  const stateQ = useQuery({
    queryKey: ['contactless', 'state', sess],
    queryFn: () => call('GET', '/contactless/session/state', { session: sess, schema: ContactlessState }),
    enabled: sess !== null,
    refetchInterval: 10_000,
    retry: false,
  });
  const configQ = useQuery({
    queryKey: ['contactless', 'config', sess],
    queryFn: () => call('GET', '/contactless/session/config', { session: sess, schema: ContactlessConfig }),
    enabled: sess !== null,
    retry: false,
  });
  const endedRef = useRef(endSession);
  endedRef.current = endSession;
  useEffect(() => {
    if (stateQ.error && isSessionEnded(stateQ.error)) endedRef.current();
  }, [stateQ.error]);
  useEffect(() => {
    if (configQ.error && isSessionEnded(configQ.error)) endedRef.current();
  }, [configQ.error]);

  const forget = async () => {
    const r = rememberStore.get();
    rememberStore.clear();
    setHasRemember(false);
    setRememberedNow(false);
    if (r) await call('POST', '/contactless/public/forget', { body: { rememberToken: r } }).catch(() => undefined);
  };

  const start = startQ.data;
  const mode = start?.point?.mode ?? 'both';
  const alert = stateQ.data && stateQ.data.alert.status === 'active' ? stateQ.data : null;
  const open = session?.openVisit ?? null;

  let body: ReactNode;
  if (startQ.isPending) body = <PageLoading />;
  else if (startQ.isError || !start) body = <LinkProblem error={startQ.error} onRetry={() => void startQ.refetch()} />;
  else if (!session || !sess) {
    body = (
      <StartCard
        start={start}
        kind={kind}
        token={token}
        ended={ended}
        hasRemember={hasRemember}
        onForget={() => void forget()}
        onSession={(s) => {
          setEnded(false);
          setSession(s);
        }}
      />
    );
  } else if (signedOut) {
    body =
      signedOut.status === 'refused' ? <Alert>{t('w21.refused')}</Alert> : <Thanks message={signedOut.thankYouMessage || t('w21.thanks.outDefault')} remembered={false} onForget={() => undefined} canSignOut={false} onSignOut={() => undefined} outDone />;
  } else if (signedIn && signedIn.result.status === 'refused') {
    body = (
      <Section>
        <h1 className="text-2xl font-semibold">{t('w21.refused.title')}</h1>
        <p className="text-lg">{t('w21.refused')}</p>
        <Button className="min-h-12 w-full text-base" onClick={() => setSignedIn(null)}>{t('w21.refused.retry')}</Button>
      </Section>
    );
  } else if (open && mode !== 'in' && (wantsOut || !signedIn)) {
    body = (
      <SignOutCard
        session={sess}
        config={configQ.data}
        open={open}
        timeZone={configQ.data?.timeZone ?? 'Europe/London'}
        onDone={(r) => {
          setSignedOut(r);
          if (r.status !== 'refused') setSession({ ...session, openVisit: null });
        }}
      />
    );
  } else if (signedIn || open) {
    body = (
      <Thanks
        message={signedIn?.result.thankYouMessage ?? t('w21.thanks.alreadyIn')}
        remembered={rememberedNow}
        onForget={() => void forget()}
        canSignOut={mode !== 'in'}
        onSignOut={() => setWantsOut(true)}
        outDone={false}
      />
    );
  } else if (mode === 'out') {
    body = (
      <Section>
        <p className="text-lg">{t('w21.noOpenVisit')}</p>
      </Section>
    );
  } else if (configQ.isPending) body = <PageLoading />;
  else if (configQ.isError || !configQ.data) body = <LinkProblem error={configQ.error} onRetry={() => void configQ.refetch()} />;
  else {
    body = (
      <Section>
        <h1 className="text-2xl font-semibold">{t('w21.signIn.title', { site: configQ.data.siteName })}</h1>
        <SignInFlow
          config={configQ.data}
          session={sess}
          remembered={session.remembered}
          hasRememberToken={hasRemember}
          onForget={() => void forget()}
          onDone={(s) => {
            setSignedIn(s);
            const r = s.result;
            if (r.status !== 'refused' && r.visitId) {
              if (r.rememberToken) {
                rememberStore.set(r.rememberToken);
                setHasRemember(true);
                setRememberedNow(true);
              }
              setSession({ ...session, openVisit: { visitId: r.visitId, signedInAt: new Date().toISOString(), groupName: s.groupName } });
            }
          }}
        />
      </Section>
    );
  }

  return (
    <Shell token={token} start={start}>
      {sess && alert ? <EvacuationAlert session={sess} state={alert} /> : null}
      {body}
    </Shell>
  );
}
